import type { LucideIcon } from 'lucide-react'
import {
  ArrowUpRight,
  Cloud,
  KeyRound,
  Lock,
  MapPin,
  ReceiptText,
  ScrollText,
  Server,
  Shield,
  ShieldCheck,
  Timer,
  Users,
} from 'lucide-react'

/**
 * Compliance badges shown on the homepage Enterprise strip. Only what is
 * claimed on /trust-center is rendered. The visual idiom is an icon-plus-label
 * pill, more credible than a text chip and honest about not being an official
 * trademark seal.
 */
type ComplianceBadge = {
  icon: LucideIcon
  label: string
  sub?: string
}

const complianceBadges: ComplianceBadge[] = [
  { icon: Shield,       label: 'ISO 27001',        sub: 'Certified' },
  { icon: Lock,         label: 'GDPR',             sub: 'Compliant' },
  { icon: MapPin,       label: 'EU data',          sub: 'Residency' },
  { icon: ShieldCheck,  label: 'Monitored',        sub: 'by Vanta' },
]

const controls: { icon: LucideIcon; label: string; detail: string }[] = [
  { icon: KeyRound,     label: 'RBAC + REST APIs',        detail: 'Teams, projects, API keys, scoped at the role level.' },
  { icon: Users,        label: 'SCIM + SSO',              detail: 'Okta / Azure AD / Google. Group → team auto-assignment.' },
  { icon: ReceiptText,  label: 'Cost-center attribution', detail: 'Spend tracked across members, teams, projects.' },
  { icon: ScrollText,   label: 'Audit log → SIEM',        detail: 'Every prompt change, eval edit, key event, signed.' },
  { icon: Timer,        label: 'Custom retention policy', detail: 'Configure retention windows and deletion.' },
]

const deployments = [
  {
    icon: Server,
    title: 'Self-hosted',
    detail: 'Docker, Kubernetes/Helm, or in your VPC',
  },
  {
    icon: Lock,
    title: 'Hybrid',
    detail: 'Data plane on your infra, control plane on ours',
  },
  {
    icon: Cloud,
    title: 'Cloud',
    detail: 'Managed multi-tenant SaaS · EU / US / UK / APAC',
  },
]

export function SectionEnterprise() {
  return (
    <section id="enterprise" data-nav-dark className="relative scroll-mt-0 overflow-hidden text-paper">
      <div className="container-rail relative z-10 py-20 md:py-28">
        {/* Section thesis. Three chapters answer the three questions every
            enterprise asks: where the data runs, who controls it, what
            certifies it. The H2 telegraphs all three in parallel form. */}
        <div>
          <h2 className="display text-[30px] leading-[1.06] text-paper md:text-[44px]">
            Where it <span className="italic">runs</span>.{' '}
            <span className="text-paper/55">
              Who <span className="italic">controls</span> it.
            </span>{' '}
            <span className="text-paper/35">
              What <span className="italic">certifies</span> it.
            </span>
          </h2>
          <p className="mt-7 max-w-[640px] text-[17px] leading-[1.6] text-paper/65 md:text-[18px]">
            LangWatch deploys where your data lives, enforces who can touch
            it, and brings the certifications your security review needs.
          </p>
        </div>

        {/* Three columns, all visible at one glance. Each answers one of the
            three questions every enterprise asks. */}
        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
          {/* ─── 01 · YOUR DATA ─── */}
          <div className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur">
            <h3 className="text-[18px] font-semibold tracking-tight text-paper md:text-[19px]">
              Cloud, self-hosted, or hybrid.
            </h3>
            <ul className="mt-4 space-y-3.5">
              {deployments.map((d) => {
                const Icon = d.icon
                return (
                  <li key={d.title} className="flex items-start gap-3">
                    <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white/[0.06] text-brand-300">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <div>
                      <div className="text-[14px] font-semibold text-paper">
                        {d.title}
                      </div>
                      <div className="mt-0.5 text-[12.5px] leading-[1.5] text-paper/60">
                        {d.detail}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
            <div className="mt-auto pt-5">
              <a
                href="/enterprise"
                className="inline-flex items-center gap-1 text-[12.5px] text-paper/65 transition hover:text-paper"
              >
                Deployment options
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          {/* ─── 02 · YOUR CONTROLS ─── */}
          <div className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur">
            <h3 className="text-[18px] font-semibold tracking-tight text-paper md:text-[19px]">
              Enterprise security controls
            </h3>
            <ul className="mt-4 space-y-2.5 text-[13px] text-paper/75">
              {controls.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2.5">
                  <Icon className="h-3.5 w-3.5 shrink-0 text-brand-300" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-5">
              <a
                href="/get-a-demo"
                className="inline-flex items-center gap-1 text-[12.5px] text-paper/65 transition hover:text-paper"
              >
                Book a call
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          {/* ─── 03 · YOUR COMPLIANCE ─── */}
          <div className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur">
            <h3 className="text-[18px] font-semibold tracking-tight text-paper md:text-[19px]">
              Passes your procurement review
            </h3>
            <ul className="mt-4 space-y-2.5">
              {complianceBadges.map(({ icon: Icon, label, sub }) => (
                <li key={label + sub} className="flex items-center gap-2.5">
                  <Icon className="h-3.5 w-3.5 shrink-0 text-brand-300" strokeWidth={2} />
                  <span className="text-[13px] font-medium text-paper">{label}</span>
                  {sub && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-paper/45">
                      {sub}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-5">
              <a
                href="/trust-center"
                className="inline-flex items-center gap-1 text-[12.5px] text-paper/65 transition hover:text-paper"
              >
                Full trust center
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
